<template>
  <div class="workflow-steps">
    <!-- Add FloatingActionBar for bulk actions -->
    <FloatingActionBar
      :selected="selected"
      :totalItems="totalItems"
      :showDeleteSelected="true"
      :show-assign="false"
      :showDeleteAll="false"
      :showUpdateStatus="false"
      :showSelectAll="false"
      @delete-selected="handleShowDialogDelete"
    />
    <ActitiveTabcomponent :tabs="mainTabs" :defaultTab="'list'" @tab-changed="handleTabChange">
      <template #tab-buttons>
        <div class="flex justify-end">
          <v-btn
            v-if="currentTab === 'list'"
            color="primary"
            class="normal-case"
            @click="openWorkflowDialog()"
          >
            <v-icon left>mdi-plus</v-icon>
            Add Workflow
          </v-btn>
          <v-btn
            v-if="currentTab === 'loan-rules'"
            color="primary"
            class="normal-case"
            @click="openLoanRuleDialog()"
          >
            <v-icon left>mdi-plus</v-icon>
            Add Loan Rule
          </v-btn>
        </div>
      </template>

      <template #list>
        <div class="pt-2">
          <v-data-table
            :headers="headers"
            :items="workflowTemplates"
            :loading="loading"
            :options.sync="options"
            :server-items-length="totalItems"
            :footer-props="{
              'items-per-page-options': [5, 10, 15, 30]
            }"
            class="text-sm table-radius border border-[#e0e0e0]"
            v-model="selected"
            disable-sort
            show-select
            item-key="_id"
          >
            <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
              <p
                :key="index"
                class="font-title"
                v-if="header.text?.length > 15"
                v-tooltip="{ text: header.text }"
              >
                {{ header.text }}
              </p>
              <p :key="index" v-else class="font-title">{{ header.text }}</p>
            </template>
            <template v-slot:item.name="{ item }">
              <td class="px-4 py-3">
                {{ item.name }}
              </td>
            </template>
            <template v-slot:item.type="{ item }">
              <td class="px-4 py-3">
                {{ formatType(item.type) }}
              </td>
            </template>

            <template v-slot:item.stepsCount="{ item }">
              <td class="px-4 py-3">
                <div>
                  {{ item.steps ? item.steps.length : 0 }} steps
                  <v-icon
                    size="20"
                    right
                    color="primary"
                    class="corsur-pointer"
                    v-tooltip="{ text: `${getStepsTooltip(item)}`, width: '450px' }"
                    >mdi-information</v-icon
                  >
                </div>
              </td>
            </template>

            <template v-slot:item.isActive="{ item }">
              <td class="px-4 py-3 font-weight-medium" :class="getPartnerStatusClr(item.isActive)">
                {{ item.isActive ? 'Active' : 'Inactive' }}
              </td>
            </template>

            <template v-slot:item.action="{ item }">
              <td class="px-4 py-3">
                <action-button
                  :item="item"
                  :canEdit="true"
                  :canWrite="true"
                  :canDelete="true"
                  :handleEdit="() => openWorkflowDialog(item)"
                  :handleDelete="() => deleteWorkflowStatus(item)"
                />
              </td>
            </template>
          </v-data-table>
        </div>
      </template>

      <template #loan-rules>
        <div class="pt-2">
          <v-data-table
            :headers="loanRuleHeaders"
            :items="loanRules"
            :loading="loanRulesLoading"
            :options.sync="loanRulesOptions"
            :server-items-length="loanRulesTotal"
            class="text-sm table-radius border border-[#e0e0e0]"
            disable-sort
          >
            <template v-slot:item.workflowTemplateId="{ item }">
              {{ getWorkflowTemplateName(item.workflowTemplateId) }}
            </template>
            <template v-slot:item.loanAmount="{ item }">
              ₹{{ item.loanConfiguration?.loanAmount?.min }} - ₹{{
                item.loanConfiguration?.loanAmount?.max
              }}
            </template>
            <template v-slot:item.isActive="{ item }">
              <span :class="getPartnerStatusClr(item.isActive)">
                {{ item.isActive ? 'Active' : 'Inactive' }}
              </span>
            </template>
            <template v-slot:item.action="{ item }">
              <action-button
                :item="item"
                :canEdit="true"
                :canWrite="true"
                :canDelete="true"
                :handleEdit="() => openLoanRuleDialog(item)"
                :handleDelete="() => deleteLoanRule(item)"
              />
            </template>
          </v-data-table>
        </div>
      </template>
    </ActitiveTabcomponent>

    <!-- Workflow Template Dialog -->
    <v-dialog v-model="workflowDialog" max-width="1100px" persistent>
      <v-card class="workflow-form-card" flat>
        <!-- Header -->
        <div class="form-header">
          <h3 class="form-title">
            {{ editingWorkflow._id ? 'Edit Workflow Template' : 'Add Workflow Template' }}
          </h3>
          <v-btn icon @click="closeWorkflowDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider></v-divider>

        <!-- Form Content -->
        <div class="form-content">
          <v-form ref="workflowForm" v-model="workflowFormValid" lazy-validation>
            <!-- Basic Information Section -->
            <div class="form-section">
              <h4 class="section-title">
                <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
                Basic Information
              </h4>

              <div class="section-content">
                <v-row>
                  <v-col cols="12" md="4">
                    <v-autocomplete
                      v-model="editingWorkflow.useCase"
                      :items="useCaseOptions"
                      label="Use Case"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-link-variant"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-autocomplete
                      v-model="editingWorkflow.subCase"
                      :items="subCaseOptions"
                      label="Sub Case"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-briefcase-outline"
                    ></v-autocomplete>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select
                      v-model="editingWorkflow.type"
                      label="Template Type"
                      :items="filteredWorkflowTypes"
                      item-text="text"
                      item-value="value"
                      :rules="[(v) => !!v || 'Type is required']"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-tag-outline"
                      placeholder="Select template type"
                    ></v-select>
                  </v-col>
                </v-row>

                <v-row class="mt-2">
                  <v-col cols="12">
                    <v-text-field
                      v-model="editingWorkflow.name"
                      label="Template Name"
                      :rules="[(v) => !!v || 'Name is required']"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-format-title"
                      placeholder="Enter template name"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <!-- Removed advance rules from workflow template as they now belong to Loan Rules tab -->
              </div>
            </div>

            <!-- Workflow Steps Section -->
            <div class="form-section" v-if="editingWorkflow.type">
              <div class="section-title-with-action">
                <h4 class="section-title">
                  <v-icon color="primary" class="mr-2">mdi-timeline-outline</v-icon>
                  Workflow Steps
                </h4>
              </div>

              <div class="section-content">
                <div multiple class="workflow-panels">
                  <div
                    v-for="(step, index) in editingWorkflow.steps"
                    :key="index"
                    class="workflow-step-panel"
                  >
                    <div class="workflow-panel-header">
                      <div class="workflow-header-content">
                        <div class="step-info">
                          <span class="step-name"
                            >Step <span class="step-number">{{ index + 1 }}</span></span
                          >
                          <v-chip
                            x-small
                            v-if="step.emiFrequency"
                            class="status-chip ml-1"
                            color="info"
                            text-color="white"
                          >
                            {{ step.emiFrequency * 30 }} days
                          </v-chip>
                        </div>
                        <v-btn icon x-small @click.stop="removeStep(index)" class="remove-step-btn">
                          <v-icon small>mdi-close</v-icon>
                        </v-btn>
                      </div>
                    </div>

                    <div class="workflow-panel-content">
                      <v-row>
                        <v-col cols="6" class="pb-0">
                          <v-autocomplete
                            v-model="step.name"
                            label="Select Step"
                            :items="workflowStepOptions"
                            :rules="[(v) => !!v || 'Step Name is required']"
                            outlined
                            dense
                            hide-details
                            prepend-inner-icon="mdi-format-title"
                            placeholder="Select a workflow step"
                            @change="initializePredefinedRequirements(step)"
                          ></v-autocomplete>
                        </v-col>

                        <v-col cols="6" class="pb-0" v-if="editingWorkflow.type === 'EMI_LEVEL'">
                          <v-select
                            v-model.number="step.emiFrequency"
                            label="EMI Frequency (in days)"
                            :items="emiFrequencyOptions"
                            type="number"
                            outlined
                            dense
                            hide-details
                            prepend-inner-icon="mdi-calendar-clock"
                            placeholder="Enter EMI frequency"
                          ></v-select>
                        </v-col>

                        <v-col cols="12" md="6" class="pt-0">
                          <v-checkbox
                            v-model="step.isRequired"
                            label="Mandatory to complete"
                            color="primary"
                            hide-details
                          ></v-checkbox>
                        </v-col>

                        <!-- Requirements Section -->
                        <v-col cols="12" class="p-0">
                          <div class="requirements-section">
                            <div class="section-title-with-action">
                              <h5 class="requirements-title mb-0">Requirements</h5>
                            </div>

                            <!-- Pre-defined Requirements -->
                            <div
                              v-if="getPredefinedRequirements(step.name).length > 0"
                              class="predefined-requirements mb-3"
                            >
                              <div class="text-caption text--secondary">
                                Pre-defined Requirements:
                              </div>

                              <v-card
                                v-for="(req, reqIndex) in getPredefinedRequirements(step.name)"
                                :key="`predefined-${index}-${reqIndex}`"
                                flat
                                class="requirement-card mb-2"
                              >
                                <v-checkbox
                                  v-model="step.predefinedRequirements[req].added"
                                  label=""
                                  class="pt-0"
                                  color="primary"
                                  hide-details
                                ></v-checkbox>
                                <div class="flex justify-between pa-3">
                                  <div class="d-flex align-center justify-space-between">
                                    <span class="font-weight-medium">{{ req }}</span>
                                  </div>
                                  <div class="flex justify-end">
                                    <v-checkbox
                                      v-model="step.predefinedRequirements[req].isRequired"
                                      label="Required"
                                      color="primary"
                                      dense
                                      class="mt-0 pt-0"
                                      hide-details
                                    ></v-checkbox>
                                    <v-divider vertical class="mx-2"></v-divider>
                                    <v-checkbox
                                      v-model="step.predefinedRequirements[req].isDocument"
                                      label="Document"
                                      color="primary"
                                      dense
                                      class="mt-0 pt-0"
                                      hide-details
                                    ></v-checkbox>
                                  </div>
                                </div>
                              </v-card>
                            </div>

                            <!-- Custom Requirements -->
                            <div
                              v-if="step.customRequirements && step.customRequirements.length > 0"
                              class="custom-requirements"
                            >
                              <div class="text-caption text--secondary mb-2">
                                Custom Requirements:
                              </div>
                              <v-card
                                v-for="(req, reqIndex) in step.customRequirements"
                                :key="`custom-${index}-${reqIndex}`"
                                outlined
                                class="requirement-card mb-2 position-relative"
                              >
                                <!-- Delete button positioned at top right -->
                                <v-btn
                                  icon
                                  x-small
                                  color="error"
                                  @click="removeCustomRequirement(index, reqIndex)"
                                  class="delete-req-btn-top-right"
                                >
                                  <v-icon x-small>mdi-close</v-icon>
                                </v-btn>

                                <div class="flex justify-between pa-3">
                                  <div class="flex justify-between">
                                    <v-text-field
                                      v-model="req.name"
                                      label="Requirement Name"
                                      outlined
                                      dense
                                      hide-details
                                    ></v-text-field>
                                    <div class="flex justify-end">
                                      <v-checkbox
                                        v-model="req.isRequired"
                                        label="Required"
                                        color="primary"
                                        dense
                                        hide-details
                                        class="mt-0 pt-0"
                                      ></v-checkbox>
                                      <v-divider vertical class="mx-2"></v-divider>
                                      <v-checkbox
                                        v-model="req.isDocument"
                                        label="Document"
                                        color="primary"
                                        dense
                                        class="mt-0 pt-0"
                                        hide-details
                                      ></v-checkbox>
                                    </div>
                                  </div>
                                </div>
                              </v-card>
                            </div>
                            <div class="flex justify-end" v-if="step.name">
                              <v-btn
                                x-small
                                color="primary"
                                outlined
                                @click="addCustomRequirement(index)"
                                class="add-step-btn"
                              >
                                <v-icon x-small class="mr-1">mdi-plus</v-icon>
                                Add Custom
                              </v-btn>
                            </div>

                            <div
                              v-if="
                                getPredefinedRequirements(step.name).length === 0 &&
                                (!step.customRequirements || step.customRequirements.length === 0)
                              "
                              class="no-requirements-message mt-2"
                            >
                              <v-icon small color="grey lighten-1"
                                >mdi-clipboard-list-outline</v-icon
                              >
                              <p class="text-caption">
                                No requirements available. Click "Add Custom" to create a custom
                                requirement.
                              </p>
                            </div>
                          </div>
                        </v-col>
                      </v-row>
                    </div>
                  </div>
                </div>

                <div
                  v-if="!editingWorkflow.steps || editingWorkflow.steps.length === 0"
                  class="no-steps-message"
                >
                  <v-icon large color="grey lighten-1">mdi-timeline-plus-outline</v-icon>
                  <p>No workflow steps added yet. Click "Add Step" to create your first step.</p>
                </div>
                <div class="flex justify-end mt-2">
                  <v-btn
                    small
                    color="primary"
                    outlined
                    @click="addStep"
                    class="add-step-btn"
                    v-if="editingWorkflow.type"
                  >
                    <v-icon small class="mr-1">mdi-plus</v-icon>
                    Add Step
                  </v-btn>
                </div>
              </div>
            </div>
          </v-form>
        </div>

        <!-- Form Actions -->
        <v-divider></v-divider>

        <div class="form-actions">
          <v-btn text @click="closeWorkflowDialog" class="normal-case cancel-btn"> Cancel </v-btn>
          <v-btn
            color="primary"
            @click="saveWorkflowTemplate"
            :loading="saving"
            :disabled="saving"
            class="save-btn normal-case"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Loan Rule Dialog -->
    <v-dialog v-model="loanRuleDialog" max-width="1100px" persistent>
      <v-card class="workflow-form-card" flat>
        <div class="form-header">
          <h3 class="form-title">
            {{ editingLoanRule._id ? 'Edit Loan Rule' : 'Add Loan Rule' }}
          </h3>
          <v-btn icon @click="closeLoanRuleDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </div>

        <v-divider></v-divider>

        <div class="form-content">
          <v-form ref="loanRuleForm" v-model="loanRuleFormValid" lazy-validation>
            <div class="form-section">
              <h4 class="section-title">
                <v-icon color="primary" class="mr-2">mdi-information-outline</v-icon>
                Basic Information
              </h4>

              <div class="section-content">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field
                      v-model="editingLoanRule.name"
                      label="Rule Name"
                      :rules="[(v) => !!v || 'Rule Name is required']"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-format-title"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-autocomplete
                      v-model="editingLoanRule.workflowTemplateId"
                      :items="workflowTemplates"
                      item-text="name"
                      item-value="_id"
                      label="Linked Workflow Template"
                      :rules="[(v) => !!v || 'Workflow Template is required']"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-timeline-outline"
                    ></v-autocomplete>
                  </v-col>
                </v-row>

                <v-row class="mt-4">
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="editingLoanRule.emiFrequency"
                      :items="['Monthly', 'Bi-Monthly', 'Quarterly', 'Half Yearly', 'Weekly']"
                      label="EMI Frequency"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-calendar-refresh"
                    ></v-select>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select
                      v-model="editingLoanRule.interestCategory"
                      :items="interestCategoryOptions"
                      label="Interest Category"
                      outlined
                      dense
                      hide-details
                      prepend-inner-icon="mdi-chart-bell-curve"
                    ></v-select>
                  </v-col>
                </v-row>

                <!-- Loan Product Terms Table -->
                <div class="mt-6">
                  <h5 class="text-subtitle-2 font-weight-bold mb-3 grey--text text--darken-2">
                    Loan Configuration Rules
                  </h5>
                  <v-simple-table dense class="border rounded terms-table">
                    <template v-slot:default>
                      <thead>
                        <tr>
                          <th class="text-left text-xs py-3 px-4">Parameter Name</th>
                          <th class="text-left text-xs py-3 px-4">Minimum</th>
                          <th class="text-left text-xs py-3 px-4">Maximum</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(term, key) in loanTermsConfig" :key="key">
                          <td class="text-xs font-weight-bold px-4">{{ term.label }}</td>
                          <td class="pa-3">
                            <v-text-field
                              v-model.number="editingLoanRule.loanConfiguration[key].min"
                              type="number"
                              dense
                              hide-details
                              outlined
                              class="text-xs compact-input"
                            ></v-text-field>
                          </td>
                          <td class="pa-3">
                            <v-text-field
                              v-model.number="editingLoanRule.loanConfiguration[key].max"
                              type="number"
                              dense
                              hide-details
                              outlined
                              class="text-xs compact-input"
                            ></v-text-field>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>
              </div>
            </div>

            <!-- Deductions Section -->
            <div class="form-section mt-8 border-t pt-4">
              <div class="d-flex justify-space-between align-center mb-3">
                <h5 class="text-subtitle-2 font-weight-bold">Deductions (VAS)</h5>
                <v-btn x-small color="primary" @click="addDeduction">
                  <v-icon x-small left>mdi-plus</v-icon> Add Deduction
                </v-btn>
              </div>
              <v-simple-table dense class="border rounded">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left text-xs px-4">VAS Type</th>
                      <th class="text-left text-xs px-4">VAS Name</th>
                      <th class="text-left text-xs px-4">Amount</th>
                      <th class="text-left text-xs px-4">VAS (%)</th>
                      <th class="text-left text-xs px-4">Waiver Amount</th>
                      <th class="text-left text-xs px-4">Waiver VAS (%)</th>
                      <th class="text-left text-xs px-4">Income Recognition</th>
                      <th class="text-center text-xs px-4" style="width: 50px">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in editingLoanRule.deductions" :key="idx">
                      <td class="pa-2">
                        <v-select
                          v-model="item.deductionType"
                          :items="[
                            'Fees',
                            'Insurance',
                            'Legal Charges',
                            'Agreement Charges',
                            'Other'
                          ]"
                          dense
                          hide-details
                          outlined
                          class="text-xs"
                        ></v-select>
                      </td>
                      <td class="pa-2">
                        <v-text-field
                          v-model="item.deductionName"
                          dense
                          hide-details
                          outlined
                          class="text-xs"
                        ></v-text-field>
                      </td>
                      <td class="pa-2">
                        <v-text-field
                          v-model.number="item.deductionAmount"
                          type="number"
                          dense
                          hide-details
                          outlined
                          class="text-xs"
                        ></v-text-field>
                      </td>
                      <td class="pa-2">
                        <v-text-field
                          v-model.number="item.deductionPercentage"
                          type="number"
                          dense
                          hide-details
                          outlined
                          class="text-xs"
                        ></v-text-field>
                      </td>
                      <td class="pa-2">
                        <v-text-field
                          v-model.number="item.waiverAmount"
                          type="number"
                          dense
                          hide-details
                          outlined
                          class="text-xs"
                        ></v-text-field>
                      </td>
                      <td class="pa-2">
                        <v-text-field
                          v-model.number="item.waiverPercentage"
                          type="number"
                          dense
                          hide-details
                          outlined
                          class="text-xs"
                        ></v-text-field>
                      </td>
                      <td class="pa-2">
                        <v-select
                          v-model="item.incomeRecognition"
                          :items="incomeRecognitionOptions"
                          dense
                          hide-details
                          outlined
                          class="text-xs"
                        ></v-select>
                      </td>
                      <td class="text-center pa-2">
                        <v-btn icon x-small color="error" @click="removeDeduction(idx)">
                          <v-icon x-small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr
                      v-if="!editingLoanRule.deductions || editingLoanRule.deductions.length === 0"
                    >
                      <td colspan="8" class="text-center text-caption grey--text py-2">
                        No deductions added
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <!-- Bounce Charge Settings -->
            <div class="form-section mt-8 border-t pt-4">
              <div class="d-flex justify-space-between align-center mb-3">
                <h5 class="text-subtitle-2 font-weight-bold">Bounce Charge Settings</h5>
                <v-btn x-small color="primary" @click="addBounceCharge">
                  <v-icon x-small left>mdi-plus</v-icon> Add Bounce Rule
                </v-btn>
              </div>
              <v-simple-table dense class="border rounded">
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left text-xs px-4">Bounce Type</th>
                      <th class="text-left text-xs px-4">Amount</th>
                      <th class="text-left text-xs px-4">Income Recognition</th>
                      <th class="text-center text-xs px-4" style="width: 50px">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in editingLoanRule.bounceCharges" :key="idx">
                      <td class="pa-2">
                        <v-select
                          v-model="item.bounceType"
                          :items="bounceTypeOptions"
                          dense
                          hide-details
                          outlined
                          class="text-xs"
                        ></v-select>
                      </td>
                      <td class="pa-2">
                        <v-text-field
                          v-model.number="item.bounceAmount"
                          type="number"
                          dense
                          hide-details
                          outlined
                          class="text-xs"
                        ></v-text-field>
                      </td>
                      <td class="pa-2">
                        <v-select
                          v-model="item.incomeRecognition"
                          :items="incomeRecognitionOptions"
                          dense
                          hide-details
                          outlined
                          class="text-xs"
                        ></v-select>
                      </td>
                      <td class="text-center pa-2">
                        <v-btn icon x-small color="error" @click="removeBounceCharge(idx)">
                          <v-icon x-small>mdi-delete</v-icon>
                        </v-btn>
                      </td>
                    </tr>
                    <tr
                      v-if="
                        !editingLoanRule.bounceCharges || editingLoanRule.bounceCharges.length === 0
                      "
                    >
                      <td colspan="4" class="text-center text-caption grey--text py-2">
                        No bounce rules added
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
            </div>

            <!-- Foreclosure & Pre-payment -->
            <v-row class="mt-6 px-4 pb-4">
              <v-col cols="12" md="6">
                <div class="border rounded pa-4 bg-gray-50">
                  <h5 class="text-subtitle-2 font-weight-bold mb-4 grey--text text--darken-3">
                    <v-icon small class="mr-1">mdi-lock-open-outline</v-icon>
                    Foreclosure Charge
                  </h5>
                  <v-row dense>
                    <v-col cols="6">
                      <v-select
                        v-model="editingLoanRule.foreClosure.closureType"
                        :items="foreclosureChargeOptions"
                        label="Charge Type"
                        outlined
                        dense
                        hide-details
                        class="text-xs"
                      ></v-select>
                    </v-col>
                    <v-col cols="6">
                      <v-text-field
                        v-model.number="editingLoanRule.foreClosure.restrictionDays"
                        label="Restriction (Days)"
                        type="number"
                        outlined
                        dense
                        hide-details
                        class="text-xs"
                      ></v-text-field>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="border rounded pa-4 bg-gray-50">
                  <h5 class="text-subtitle-2 font-weight-bold mb-4 grey--text text--darken-3">
                    <v-icon small class="mr-1">mdi-cash-check</v-icon>
                    Pre-payment Settings
                  </h5>
                  <v-row dense>
                    <v-col cols="6">
                      <v-select
                        v-model="editingLoanRule.prePayment.prePaymenetAllowed"
                        :items="[
                          { text: 'Yes', value: true },
                          { text: 'No', value: false }
                        ]"
                        label="Allowed"
                        outlined
                        dense
                        hide-details
                        class="text-xs"
                      ></v-select>
                    </v-col>
                    <v-col cols="6">
                      <v-select
                        v-model="editingLoanRule.prePayment.backDatePaymentAllowed"
                        :items="[
                          { text: 'Yes', value: true },
                          { text: 'No', value: false }
                        ]"
                        label="Back-dated"
                        outlined
                        dense
                        hide-details
                        class="text-xs"
                      ></v-select>
                    </v-col>
                  </v-row>
                </div>
              </v-col>
            </v-row>
          </v-form>
        </div>

        <v-divider></v-divider>

        <div class="form-actions">
          <v-btn text @click="closeLoanRuleDialog" class="normal-case cancel-btn"> Cancel </v-btn>
          <v-btn
            color="primary"
            @click="saveLoanRule"
            :loading="saving"
            :disabled="saving"
            class="save-btn normal-case"
          >
            <v-icon left>mdi-content-save</v-icon>
            Save
          </v-btn>
        </div>
      </v-card>
    </v-dialog>

    <!-- Add AlertPopUp for delete confirmation -->
    <AlertPopUp
      :showConfirmationDialog="showDeleteDialog"
      :confirmationTitle="'Confirm Delete'"
      :confirmationMessage="`Do you want to delete selected workflow templates?`"
      :confirmationActionText="'DELETE'"
      :confirmationActionColor="'red'"
      :performAction="deleteSelectedWorkflowTemplates"
      :cancel="handleShowDialogDelete"
      :loading="loadingDelete"
    />
  </div>
</template>

<script>
import ActionButton from '@/components/ActionButton.vue'
import { debounce } from '@/utils/common'
import CombinedInput from '@/components/common/CombinedInput.vue'
import { chipStatusColors } from '@/utils/workforce/statusColors'
import { workFlowStepsOptions } from '@/utils/workforce/constants'
import ActitiveTabcomponent from '@/components/ActitiveTabcomponent.vue'
import FloatingActionBar from '@/components/Workforce/CollectionManagement/CaseModules/FloatingActionBar.vue'
import AlertPopUp from '@/components/AlertPopUp.vue'
import { axiosUrl } from '@/utils/workforceApi'
export default {
  name: 'WorkflowSteps',
  components: {
    ActionButton,
    ActitiveTabcomponent,
    FloatingActionBar,
    AlertPopUp
    // CombinedInput
  },
  data() {
    return {
      headers: [
        { text: 'Template Name', value: 'name' },
        { text: 'Type', value: 'type' },
        { text: 'Steps', value: 'stepsCount' },
        { text: 'Status', value: 'isActive' },
        { text: 'Actions', value: 'action', sortable: false }
      ],
      currentTab: 'list',
      selected: [],
      workflowTemplates: [],
      loading: false,
      saving: false,
      totalItems: 0,
      options: {
        page: 1,
        itemsPerPage: 10
      },
      subCaseOptions: [],
      // Dialog controls
      workflowDialog: false,

      // Loan Rules Data
      loanRules: [],
      loanRulesLoading: false,
      loanRulesTotal: 0,
      loanRulesOptions: {
        page: 1,
        itemsPerPage: 10
      },
      loanRuleHeaders: [
        { text: 'Rule Name', value: 'name' },
        { text: 'Workflow', value: 'workflowTemplateId' },
        { text: 'Frequency', value: 'emiFrequency' },
        { text: 'Loan Range', value: 'loanAmount' },
        { text: 'Status', value: 'isActive' },
        { text: 'Actions', value: 'action', align: 'center', sortable: false }
      ],
      loanRuleDialog: false,
      loanRuleFormValid: true,
      editingLoanRule: {
        name: '',
        workflowTemplateId: '',
        emiFrequency: 'Monthly',
        interestCategory: 'Diminishing',
        loanConfiguration: {
          loanAmount: { min: 0, max: 0 },
          yearlyInterestRate: { min: 0, max: 0 },
          tenure: { min: 0, max: 0 },
          sanctionedAmount: { min: 0, max: 0 }
        },
        deductions: [],
        bounceCharges: [],
        foreClosure: {
          closureType: '',
          restrictionDays: 0
        },
        prePayment: {
          prePaymenetAllowed: false,
          backDatePaymentAllowed: false
        },
        isActive: true
      },

      // Form data
      editingWorkflowStatus: null,
      editingWorkflow: {
        name: '',
        type: '',
        isActive: true,
        steps: [],
        useCase: 'loan_management',
        subCase: ''
      },
      vasTypeOptions: [
        'Insurance',
        'Legal Charges',
        'Agreement Charges',
        'Bounce Charges',
        'Other'
      ],
      incomeRecognitionOptions: [
        { text: 'Deduct Now', value: 'deduct_now' },
        { text: 'Collect Later', value: 'collect_later' },
        { text: 'Charge', value: 'charge' }
      ],
      bounceTypeOptions: [
        'Cheque Bounce',
        'NACH Bounce',
        'eNACH Bounce',
        'UPI Bounce',
        'Non-Instrumental Charges'
      ],
      foreclosureChargeOptions: ['Fixed', 'Percentage', 'None'],
      yesNoOptions: ['Yes', 'No'],
      loanTermsConfig: {
        loanAmount: { label: 'Loan Amount (₹)' },
        yearlyInterestRate: { label: 'Yearly Interest (%)' },
        tenure: { label: 'Tenure (Months)' },
        sanctionedAmount: { label: 'Sanction Amount (₹)' }
      },
      interestCategoryOptions: ['Diminishing', 'Flat'],
      useCaseOptions: [
        { text: 'Loan Management', value: 'loan_management' },
        { text: 'Collection', value: 'collection_management' }
      ],
      loanTypeOptions: [
        'UnSecured Loan',
        'Top-Up Loan',
        'Secured Loan',
        'Green Channel',
        'Direct Channel',
        'Unsecured Part',
        'Mutual Fund(Secured)',
        'Invoice Discounting',
        'Credit Line',
        'Consumer Durable',
        'Insurance (Secured)',
        'Solar Loan',
        'Salaried Credit'
      ],
      loanCategoryOptions: ['Personal', 'Home', 'Auto', 'Gold', 'Business', 'Education', 'Other'],
      leadSourceOptions: [
        'DSA',
        'Consultant',
        'Dealer',
        'Customer',
        'DST',
        'Website',
        'Referral',
        'Tele Calling',
        'Others'
      ],
      workflowFormValid: false,
      expandedStepPanel: [],
      filterWorkflow: '',
      emiFrequencyOptions: [
        { text: 'Monthly', value: 1 },
        { text: 'Bi-Monthly', value: 2 },
        { text: 'Quarterly', value: 3 },
        { text: 'Half Yearly', value: 6 },
        { text: 'Weekly', value: 0.233 }
      ],
      // Dropdown options
      workflowTypes: [
        { text: 'Loan', value: 'LOAN_LEVEL' },
        { text: 'Disbursement', value: 'DISBURSEMENT_LEVEL' },
        { text: 'EMI', value: 'EMI_LEVEL' },
        { text: 'Loan Closure', value: 'LOAN_CLOSURE_LEVEL' },
        { text: 'Accounts', value: 'ACCOUNTS_LEVEL' }
      ],
      showDeleteDialog: false,
      loadingDelete: false
    }
  },

  computed: {
    filteredWorkflowTypes() {
      const useCase = this.editingWorkflow.useCase
      // SubCase can also affect types if needed in future
      const subCase = this.editingWorkflow.subCase

      if (useCase === 'loan_management') {
        return this.workflowTypes.filter((t) =>
          ['LOAN_LEVEL', 'DISBURSEMENT_LEVEL', 'LOAN_CLOSURE_LEVEL', 'ACCOUNTS_LEVEL'].includes(
            t.value
          )
        )
      } else if (useCase === 'collection_management') {
        return this.workflowTypes.filter((t) => ['EMI_LEVEL'].includes(t.value))
      }

      return this.workflowTypes
    },
    mainTabs() {
      return [
        {
          key: 'list',
          label: 'Workflow Templates',
          icon: 'mdi-timeline-outline',
          component: 'WorkflowTemplates'
        },
        {
          key: 'loan-rules',
          label: 'Loan Rules',
          icon: 'mdi-file-percent-outline',
          component: 'LoanRules'
        }
      ]
    },
    workflowStepOptions() {
      // Determine which workflow type steps to show
      let workflowTypeKey = 'loan' // default
      if (this.editingWorkflow.type === 'DISBURSEMENT_LEVEL') {
        workflowTypeKey = 'disbursement'
      } else if (this.editingWorkflow.type === 'EMI_LEVEL') {
        workflowTypeKey = 'emi'
      } else if (this.editingWorkflow.type === 'LOAN_CLOSURE_LEVEL') {
        workflowTypeKey = 'loanClosure'
      } else if (this.editingWorkflow.type === 'ACCOUNTS_LEVEL') {
        workflowTypeKey = 'accounts'
      }

      // Get the steps for the selected workflow type
      const stepsForType = workFlowStepsOptions[workflowTypeKey]
      if (!stepsForType) {
        return []
      }

      // Create grouped options with headers and items
      const groupedOptions = []

      Object.keys(stepsForType).forEach((categoryName) => {
        // Add header
        groupedOptions.push({
          header: categoryName
        })

        // Add step options under this category
        const categorySteps = stepsForType[categoryName]
        Object.keys(categorySteps).forEach((stepName) => {
          groupedOptions.push({
            text: stepName,
            value: stepName
          })
        })
      })

      return groupedOptions
    }
  },

  async mounted() {
    await this.fetchWorkflowTemplates()
    await this.fetchLoanMapping()
  },

  watch: {
    'editingWorkflow.useCase': {
      handler(newVal) {
        if (newVal) {
          this.fetchSubCases(newVal)
        }
      },
      immediate: true
    },
    options: {
      handler() {
        this.fetchWorkflowTemplates()
      },
      deep: true
    },
    '$route.query.search': {
      async handler(search) {
        try {
          this.filterWorkflow = search || ''
          this.options.page = 1
          this.handleInput()
        } catch (error) {
          console.log(error)
        }
      },
      immediate: true
    }
  },

  methods: {
    // Add these methods for checkbox selection and bulk delete
    handleShowDialogDelete() {
      this.showDeleteDialog = !this.showDeleteDialog
      if (this.showDeleteDialog === false) {
        this.fetchWorkflowTemplates()
      }
    },
    async deleteSingleWorkflowTemplate(item) {
      try {
        const response = await this.$axios.delete(`${axiosUrl.workflow.deleteWorkflow}/${item._id}`)
        return response.data
      } catch (error) {
        console.error('Error deleting workflow template:', error)
        throw error
      }
    },
    async deleteSelectedWorkflowTemplates() {
      this.loadingDelete = true

      try {
        // Create an array of promises for all delete operations
        const deletePromises = this.selected.map((item) => this.deleteSingleWorkflowTemplate(item))

        // Execute all delete operations concurrently
        const results = await Promise.all(deletePromises)

        // Check if all operations were successful
        const allSuccessful = results.every((result) => result && result.success)

        if (allSuccessful) {
          this.$toast.success(`${this.selected.length} workflow template(s) deleted successfully`)
          // Clear selection
          this.selected = []
          // Refresh the list
          this.fetchWorkflowTemplates()
        } else {
          this.$toast.error('Some workflow templates could not be deleted')
        }

        // Close the dialog
        this.handleShowDialogDelete()
      } catch (error) {
        console.error('Error deleting workflow templates:', error)
        this.$toast.error('Failed to delete workflow templates')
      } finally {
        this.loadingDelete = false
      }
    },
    handleInput: debounce(async function () {
      this.options.page = 1
      await this.fetchWorkflowTemplates()
    }, 800),

    addDeduction() {
      const target = this.loanRuleDialog ? this.editingLoanRule : this.editingWorkflow.advanceRules
      if (!target.deductions) this.$set(target, 'deductions', [])
      target.deductions.push({
        deductionType: 'Fees',
        deductionName: '',
        deductionAmount: 0,
        deductionPercentage: 0,
        waiverAmount: 0,
        waiverPercentage: 0,
        incomeRecognition: 'deduct_now'
      })
    },

    removeDeduction(index) {
      const target = this.loanRuleDialog ? this.editingLoanRule : this.editingWorkflow.advanceRules
      target.deductions.splice(index, 1)
    },

    addBounceCharge() {
      const target = this.loanRuleDialog ? this.editingLoanRule : this.editingWorkflow.advanceRules
      if (!target.bounceCharges) this.$set(target, 'bounceCharges', [])
      target.bounceCharges.push({
        bounceType: 'Cheque Bounce',
        bounceAmount: 0,
        incomeRecognition: 'collect_later'
      })
    },

    removeBounceCharge(index) {
      const target = this.loanRuleDialog ? this.editingLoanRule : this.editingWorkflow.advanceRules
      target.bounceCharges.splice(index, 1)
    },
    async updateLoanCategoryOptions(val) {
      if (val && Array.isArray(val)) {
        val.forEach((item) => {
          if (item && typeof item === 'string' && !this.loanCategoryOptions.includes(item)) {
            this.loanCategoryOptions.push(item)
          }
        })
      }
    },

    async fetchSubCases(useCase) {
      try {
        const response = await this.$axios.get(`/orgSetup/extend-usecase?useCase=${useCase}`)
        if (response.data?.data) {
          this.subCaseOptions = response.data.data.map((item) => ({
            text: item.name,
            value: item._id
          }))
        }
      } catch (error) {
        console.error('Error fetching sub cases:', error)
      }
    },
    async fetchLoanMapping() {
      try {
        const response = await this.$axios.get(`/collection/extend-usecase/loan_management`)
        const extendUseCase = response.data?.data
        if (extendUseCase) {
          if (extendUseCase.loanTypes) {
            this.loanTypeOptions = extendUseCase.loanTypes.map((item) => item.type)
          }
          if (extendUseCase.loanCategories) {
            this.loanCategoryOptions = extendUseCase.loanCategories.map((item) => item.type)
          }
          if (extendUseCase.leadSource) {
            this.leadSourceOptions = extendUseCase.leadSource.map((item) => item.type)
          }
        }
      } catch (error) {
        console.error('Error fetching loan mapping:', error)
      }
    },

    async fetchWorkflowTemplates() {
      try {
        this.loading = true
        const { page, itemsPerPage } = this.options

        const params = {
          page,
          limit: itemsPerPage
        }

        if (this.filterWorkflow) {
          params.search = this.filterWorkflow
        }
        const response = await this.$axios.get(axiosUrl.workflow.getWorkflow, {
          params
        })

        this.workflowTemplates = response.data.templates || []
        this.totalItems = response.data.pagination?.totalCount
      } catch (error) {
        console.error('Error fetching workflow templates:', error)
        this.$toast.error('Failed to load workflow templates')
      } finally {
        this.loading = false
      }
    },

    async fetchLoanRules() {
      try {
        this.loanRulesLoading = true
        const response = await this.$axios.get(axiosUrl.loanRules.getLoanRules, {
          params: {
            page: this.loanRulesOptions.page,
            limit: this.loanRulesOptions.itemsPerPage
          }
        })
        this.loanRules = response.data.loanRules || []
        this.loanRulesTotal = response.data.pagination?.totalCount || this.loanRules.length
      } catch (error) {
        console.error('Error fetching loan rules:', error)
        this.$toast.error('Failed to load loan rules')
      } finally {
        this.loanRulesLoading = false
      }
    },

    handleTabChange(tab) {
      this.currentTab = tab
      if (tab === 'loan-rules') {
        this.fetchLoanRules()
      } else {
        this.fetchWorkflowTemplates()
      }
    },

    getWorkflowTemplateName(id) {
      const template = this.workflowTemplates.find((t) => t._id === id)
      return template ? template.name : 'Unknown'
    },

    openWorkflowDialog(workflow = null) {
      if (workflow) {
        // Deep clone the workflow to avoid direct modification
        this.editingWorkflow = JSON.parse(JSON.stringify(workflow))
        // Force useCase to loan_management
        this.editingWorkflow.useCase = 'loan_management'

        // Ensure steps array exists
        if (!this.editingWorkflow.steps) this.$set(this.editingWorkflow, 'steps', [])

        // Ensure requirements are in the right format for the form
        this.editingWorkflow.steps.forEach((step) => {
          this.$set(step, 'predefinedRequirements', {})
          this.$set(step, 'customRequirements', [])

          // Get predefined requirements for this step name and type
          const predefinedReqs = this.getPredefinedRequirements(step.name)
          const requirementsLookup = {}
          if (step.requirements) {
            step.requirements.forEach((req) => {
              requirementsLookup[req.name] = req
            })
          }

          // Initialize predefined requirements (those in the list)
          predefinedReqs.forEach((reqName) => {
            if (requirementsLookup[reqName]) {
              this.$set(step.predefinedRequirements, reqName, {
                added: true,
                isRequired: requirementsLookup[reqName].isRequired,
                isDocument: requirementsLookup[reqName].isDocument
              })
            } else {
              this.$set(step.predefinedRequirements, reqName, {
                added: false,
                isRequired: true,
                isDocument: false
              })
            }
          })

          // Initialize custom requirements (those NOT in the list)
          if (step.requirements) {
            step.requirements.forEach((req) => {
              // If it's not in the predefined list, it's a custom requirement
              if (!predefinedReqs.includes(req.name)) {
                step.customRequirements.push({
                  name: req.name,
                  isRequired: req.isRequired,
                  isDocument: req.isDocument
                })
              }
            })
          }
        })
      } else {
        // Create new workflow
        this.editingWorkflow = {
          name: '',
          type: '',
          isActive: true,
          steps: [],
          useCase: 'loan_management',
          subCase: ''
        }
      }

      // Expand first step panel if there are steps
      this.expandedStepPanel =
        this.editingWorkflow.steps && this.editingWorkflow.steps.length > 0 ? [0] : []

      this.workflowDialog = true
    },
    getStepsTooltip(item) {
      if (!item.steps?.length) return 'No activities'

      return item.steps
        .map((activity, index) => {
          // Safely access activity properties with fallbacks
          const name = activity.name || `Step ${index + 1}`
          // const Requirements = activity.requirements.map((req) => req.name) || 'Unknown'

          return `<div style="margin-bottom: 8px; padding: 4px 0; border-bottom: 1px solid rgba(255,255,255,0.2); text-align: left;">
                  <strong>${name}</strong> <br/>
                  <span>Requirements:</span>
                  <div>
                  <ul style="padding: 4px 0; color: #FF9800; text-align: left;">
                    ${
                      Array.isArray(activity.requirements) && activity.requirements.length
                        ? activity.requirements.map((req) => `<li>${req.name}</li>`).join('')
                        : '<li>Unknown</li>'
                    }
                  </ul>
                  </div>
                </div>`
        })
        .join('')
    },
    statusColor(isActive) {
      if (typeof isActive === 'boolean') {
        const status = isActive ? 'active' : 'inactive'
        const colorInfo = chipStatusColors[status]
        return colorInfo
      }

      const colorInfo = chipStatusColors[isActive?.toLowerCase()]
      return colorInfo
    },

    closeWorkflowDialog() {
      this.workflowDialog = false
      this.editingWorkflow = {
        name: '',
        type: '',
        isActive: true,
        steps: [],
        useCase: 'loan_management',
        subCase: ''
      }
    },

    openLoanRuleDialog(rule = null) {
      if (rule) {
        this.editingLoanRule = JSON.parse(JSON.stringify(rule))
      } else {
        this.editingLoanRule = {
          name: '',
          workflowTemplateId: '',
          emiFrequency: 'Monthly',
          interestCategory: 'Diminishing',
          loanConfiguration: {
            loanAmount: { min: 0, max: 0 },
            yearlyInterestRate: { min: 0, max: 0 },
            tenure: { min: 0, max: 0 },
            sanctionedAmount: { min: 0, max: 0 }
          },
          deductions: [],
          bounceCharges: [],
          foreClosure: {
            closureType: '',
            restrictionDays: 0
          },
          prePayment: {
            prePaymenetAllowed: false,
            backDatePaymentAllowed: false
          },
          isActive: true
        }
      }
      this.loanRuleDialog = true
    },

    closeLoanRuleDialog() {
      this.loanRuleDialog = false
      this.editingLoanRule = {
        name: '',
        workflowTemplateId: '',
        emiFrequency: 'Monthly',
        interestCategory: 'Diminishing',
        loanConfiguration: {
          loanAmount: { min: 0, max: 0 },
          yearlyInterestRate: { min: 0, max: 0 },
          tenure: { min: 0, max: 0 },
          sanctionedAmount: { min: 0, max: 0 }
        },
        deductions: [],
        bounceCharges: [],
        foreClosure: {
          closureType: '',
          restrictionDays: 0
        },
        prePayment: {
          prePaymenetAllowed: false,
          backDatePaymentAllowed: false
        },
        isActive: true
      }
    },

    async saveLoanRule() {
      if (!this.$refs.loanRuleForm.validate()) {
        this.$toast.error('Please fill all required fields')
        return
      }

      try {
        this.saving = true
        let response
        if (this.editingLoanRule._id) {
          response = await this.$axios.put(
            `${axiosUrl.loanRules.putLoanRule}/${this.editingLoanRule._id}`,
            this.editingLoanRule
          )
        } else {
          response = await this.$axios.post(axiosUrl.loanRules.postLoanRule, this.editingLoanRule)
        }

        this.$toast.success(
          `Loan Rule ${this.editingLoanRule._id ? 'updated' : 'created'} successfully`
        )
        this.closeLoanRuleDialog()
        this.fetchLoanRules()
      } catch (error) {
        console.error('Error saving loan rule:', error)
        this.$toast.error(error?.response?.data?.message || 'Failed to save loan rule')
      } finally {
        this.saving = false
      }
    },

    async deleteLoanRule(rule) {
      try {
        if (!confirm('Are you sure you want to delete this loan rule?')) return
        await this.$axios.delete(`${axiosUrl.loanRules.deleteLoanRule}/${rule._id}`)
        this.$toast.success('Loan rule deleted successfully')
        this.fetchLoanRules()
      } catch (error) {
        console.error('Error deleting loan rule:', error)
        this.$toast.error('Failed to delete loan rule')
      }
    },

    addStep() {
      const newStepOrder = this.editingWorkflow.steps.length + 1
      this.editingWorkflow.steps.push({
        stepId: `STEP_${newStepOrder}`,
        name: '',
        isRequired: true,
        emiFrequency: null,
        predefinedRequirements: {},
        customRequirements: []
      })

      // Expand the newly added step panel
      this.expandedStepPanel = [...this.expandedStepPanel, this.editingWorkflow.steps.length - 1]
    },

    removeStep(index) {
      this.editingWorkflow.steps.splice(index, 1)

      // Update expanded panels
      this.expandedStepPanel = this.expandedStepPanel
        .filter((i) => i !== index)
        .map((i) => (i > index ? i - 1 : i))
    },

    addCustomRequirement(stepIndex) {
      if (!this.editingWorkflow.steps[stepIndex].customRequirements) {
        this.$set(this.editingWorkflow.steps[stepIndex], 'customRequirements', [])
      }

      this.editingWorkflow.steps[stepIndex].customRequirements.push({
        name: '',
        isRequired: true,
        isDocument: false
      })
    },

    removeCustomRequirement(stepIndex, reqIndex) {
      this.editingWorkflow.steps[stepIndex].customRequirements.splice(reqIndex, 1)
    },

    getPredefinedRequirements(stepName) {
      if (!stepName) return []

      // Determine which workflow type steps to show
      let workflowTypeKey = 'loan' // default
      if (this.editingWorkflow.type === 'DISBURSEMENT_LEVEL') {
        workflowTypeKey = 'disbursement'
      } else if (this.editingWorkflow.type === 'EMI_LEVEL') {
        workflowTypeKey = 'emi'
      } else if (this.editingWorkflow.type === 'LOAN_CLOSURE_LEVEL') {
        workflowTypeKey = 'loanClosure'
      } else if (this.editingWorkflow.type === 'ACCOUNTS_LEVEL') {
        workflowTypeKey = 'accounts'
      }

      const stepsForType = workFlowStepsOptions[workflowTypeKey]

      // Find the step in any category
      for (const categoryName in stepsForType) {
        const categorySteps = stepsForType[categoryName]
        if (categorySteps[stepName]) {
          return categorySteps[stepName] || []
        }
      }

      return []
    },

    initializePredefinedRequirements(step) {
      const predefinedReqs = this.getPredefinedRequirements(step.name)
      if (!step.predefinedRequirements) {
        this.$set(step, 'predefinedRequirements', {})
      }

      predefinedReqs.forEach((req) => {
        if (!step.predefinedRequirements[req]) {
          this.$set(step.predefinedRequirements, req, {
            added: true,
            isRequired: true,
            isDocument: false
          })
        }
      })
    },

    async saveWorkflowTemplate() {
      if (!this.$refs.workflowForm.validate()) {
        this.$toast.error('Please fill all required fields')
        return
      }

      try {
        this.saving = true

        const payload = {
          name: this.editingWorkflow.name,
          type: this.editingWorkflow.type,
          isActive: this.editingWorkflow.isActive,
          useCase: this.editingWorkflow.useCase,
          subCase: this.editingWorkflow.subCase,
          steps: this.editingWorkflow.steps.map((step) => {
            // Collect requirements for this specific step
            const stepRequirements = []

            // Add predefined requirements for this step
            if (step.predefinedRequirements) {
              Object.keys(step.predefinedRequirements).forEach((reqName) => {
                const reqData = step.predefinedRequirements[reqName]

                if (reqData?.added) {
                  stepRequirements.push({
                    name: reqName,
                    isRequired: reqData?.isRequired,
                    isDocument: reqData?.isDocument
                  })
                }
              })
            }

            // Add custom requirements for this step
            if (step.customRequirements && step.customRequirements.length > 0) {
              step.customRequirements.forEach((req) => {
                if (req.name && req.name.trim()) {
                  stepRequirements.push({
                    name: req.name,
                    isRequired: req?.isRequired,
                    isDocument: req?.isDocument
                  })
                }
              })
            }

            return {
              stepId: step.stepId,
              name: step.name,
              isRequired: step?.isRequired,
              emiFrequency: step?.emiFrequency || null,
              requirements: stepRequirements,
              assignmentRules: {
                autoAssign: false
              },
              conditions: []
            }
          })
        }

        let response
        if (this.editingWorkflow._id) {
          // Update existing template
          response = await this.$axios.put(
            `${axiosUrl.workflow.putWorkflow}/${this.editingWorkflow._id}`,
            payload
          )
        } else {
          // Create new template
          response = await this.$axios.post(`${axiosUrl.workflow.postWorkflow}`, payload)
        }

        this.$toast.success(
          `Workflow template ${this.editingWorkflow._id ? 'updated' : 'created'} successfully`
        )
        this.closeWorkflowDialog()
        this.fetchWorkflowTemplates()
      } catch (error) {
        console.error('Error saving workflow template:', error)
        const errorMessage = error?.response?.data?.message || 'Failed to save workflow template'
        this.$toast.error(errorMessage)
      } finally {
        this.saving = false
      }
    },

    async deleteWorkflowStatus(workflow) {
      try {
        await this.$axios.delete(`${axiosUrl.workflow.deleteWorkflow}/${workflow._id}`)
        const index = this.workflowTemplates.findIndex((w) => w._id === workflow._id)
        if (index !== -1) {
          this.workflowTemplates.splice(index, 1)
        }
        this.$toast.success('Workflow template deleted successfully')
      } catch (error) {
        console.error('Error deleting workflow:', error)
        this.$toast.error('Failed to delete workflow template')
      }
    },
    getPartnerStatusClr(status) {
      // Handle boolean values directly
      if (typeof status === 'boolean') {
        return status ? 'text-green-500' : 'text-red-500'
      }

      // Handle string values
      const statuses = {
        ACTIVE: 'text-green-500',
        IN_ACTIVE: 'text-red-500',
        active: 'text-green-500',
        inactive: 'text-red-500'
      }

      return statuses[status] || 'text-gray-500'
    },

    // Helper methods
    formatType(type) {
      switch (type) {
        case 'LOAN_LEVEL':
          return 'Loan'
        case 'DISBURSEMENT_LEVEL':
          return 'Disbursement'
        case 'EMI_LEVEL':
          return 'EMI'
        case 'LOAN_CLOSURE_LEVEL':
          return 'Loan Closure'
        case 'ACCOUNTS_LEVEL':
          return 'Accounts'
        default:
          return type
      }
    }
  }
}
</script>

<style scoped>
/* .workflow-steps {
  padding: 20px;
} */

.workflow-form-card {
  border-radius: 4px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05) !important;
  margin: 0 auto;
}

.form-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  background-color: #f8f9fa;
}

.form-title {
  font-size: 1.25rem;
  padding: 0px;
  font-weight: 600;
  color: #21427d;
  margin: 0;
}

.form-content {
  padding: 24px 32px;
  max-height: 80vh;
  overflow-y: auto;
}

.form-section {
  margin-bottom: 24px;
}

.section-title {
  font-size: 1rem;
  font-weight: 500;
  color: #21427d;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
}

.section-title-with-action {
  display: flex;
  justify-content: space-between;
  align-items: center;
  /* margin-bottom: 16px; */
}

.section-content {
  padding-left: 8px;
}

.add-step-btn {
  text-transform: none;
  font-weight: 500;
}

.workflow-panels {
  background: transparent;
}

.workflow-step-panel {
  margin-bottom: 12px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: none !important;
}

.workflow-step-panel:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05) !important;
}

.workflow-panel-header {
  padding: 12px 16px;
  min-height: 48px;
  background-color: #f8f9fa;
}

.workflow-header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.step-info {
  display: flex;
  align-items: center;
}

.step-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: #21427d;
  color: white;
  font-size: 0.75rem;
  font-weight: 600;
  margin-right: 12px;
}

.step-name {
  font-weight: 500;
  margin-right: 12px;
}

.status-chip {
  font-size: 0.625rem;
  height: 20px !important;
}

.workflow-panel-content {
  padding: 16px;
}

.no-steps-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px dashed #e0e0e0;
}

.no-steps-message p {
  margin-top: 16px;
  color: #6c757d;
  text-align: center;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  padding: 16px;
  background-color: #f8f9fa;
}

.cancel-btn {
  margin-right: 12px;
}

.save-btn {
  min-width: 120px;
  text-transform: none;
  font-weight: 500;
}

.requirements-section {
  border-top: 1px dashed #e0e0e0;
  padding: 6px;
}

.requirements-title {
  font-size: 0.9rem;
  font-weight: 500;
  color: #21427d;
}

.requirement-card {
  border-radius: 4px;
  overflow: hidden;
  display: flex;
}

.requirement-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.requirement-number {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: #21427d;
  color: white;
  font-size: 0.7rem;
  font-weight: 600;
  margin-right: 8px;
}

.requirement-name {
  font-weight: 500;
  font-size: 0.9rem;
}

.no-requirements-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 4px;
  border: 1px dashed #e0e0e0;
}

.no-requirements-message p {
  margin-top: 8px;
  color: #6c757d;
  text-align: center;
  font-size: 0.8rem;
}

/* Dark mode support */
:deep(.theme--dark) .form-header,
:deep(.theme--dark) .form-actions,
:deep(.theme--dark) .workflow-panel-header,
:deep(.theme--dark) .requirement-header {
  background-color: #2a2a2a;
}

:deep(.theme--dark) .form-title {
  color: #8cb6e1;
}

:deep(.theme--dark) .section-title,
:deep(.theme--dark) .requirements-title {
  color: #8cb6e1;
}

:deep(.theme--dark) .no-steps-message,
:deep(.theme--dark) .no-requirements-message {
  background-color: #2a2a2a;
}

/* Timeline view styles */
.workflow-timeline {
  padding: 12px;
}

.timeline-item {
  margin-bottom: 16px;
}

.timeline-order {
  font-weight: 600;
  font-size: 1rem;
  color: #21427d;
}

.timeline-content {
  padding: 12px 16px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

.step-title {
  font-size: 1rem;
  color: #21427d;
}

.step-description {
  font-size: 0.875rem;
  color: #6c757d;
}

.requirements-container {
  border-top: 1px dashed #e0e0e0;
  padding-top: 12px;
}

.requirements-chips,
.generic-steps-chips {
  display: flex;
  flex-wrap: wrap;
}

.generic-steps-container {
  border-top: 1px dashed #e0e0e0;
  padding-top: 12px;
}
.table-radius {
  border-radius: 0px !important;
}

/* Fix v-timeline alignment */
:deep(.v-timeline--dense .v-timeline-item__body) {
  max-width: calc(100% - 80px);
}

:deep(.v-timeline--dense .v-timeline-item__opposite) {
  flex: none;
  max-width: 60px;
  text-align: center;
}

:deep(.v-timeline--dense:before) {
  left: 60px;
}

:deep(.v-timeline-item__dot--small) {
  left: 60px;
}

/* Dark mode support for timeline */
:deep(.theme--dark) .timeline-content {
  background-color: #2a2a2a;
  border-color: #424242;
}

:deep(.theme--dark) .timeline-order,
:deep(.theme--dark) .step-title {
  color: #8cb6e1;
}

:deep(.theme--dark) .step-description {
  color: #adb5bd;
}

:deep(.theme--dark) .requirements-container,
:deep(.theme--dark) .generic-steps-container {
  border-color: #424242;
}

/* Delete button styles */
.delete-req-btn-top-right {
  position: absolute;
  top: -3px;
  right: 0px;
  z-index: 11;
  background-color: rgba(255, 255, 255, 0.9);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.delete-req-btn-top-right:hover {
  background-color: rgba(244, 67, 54, 0.1);
}

.requirement-card {
  position: relative;
}

/* Workflow step dropdown styles */
.workflow-step-header {
  font-weight: 800;
  font-size: 0.875rem;
  color: #21427d;
  padding: 8px 16px;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
  margin: 0 -16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.workflow-step-item {
  padding: 4px 0;
  font-size: 0.875rem;
  color: #495057;
}

:deep(.theme--dark) .workflow-step-header {
  color: #8cb6e1;
  background-color: #2a2a2a;
  border-color: #424242;
}

:deep(.theme--dark) .workflow-step-item {
  color: #adb5bd;
}

/* Workflow info section styles */
.workflow-info-section {
  margin-bottom: 16px;
}

.workflow-info-section .v-card {
  background-color: #f8f9fa;
  border: 1px solid #e0e0e0;
}

:deep(.theme--dark) .workflow-info-section .v-card {
  background-color: #2a2a2a;
  border-color: #424242;
}
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}
/* .v-data-table >>> .v-data-table__wrapper > table > thead > tr > th {
  font-size: 0.875rem;
  color: #000 !important;
} */
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.terms-table thead th {
  background-color: #f1f4f9;
  color: #21427d !important;
  font-weight: 700 !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.compact-input :deep(.v-input__slot) {
  min-height: 32px !important;
  padding: 0 8px !important;
}

.compact-input :deep(.v-text-field__slot input) {
  padding: 4px 0 !important;
}
</style>
